import { useMemo } from 'react'

import { selectCookieConsentCountries } from 'root-redux/selects/common'
import {
  selectCookieConsent,
  selectUserCountryCode,
  selectUserStateCode,
} from 'root-redux/selects/user'

import { useBulkSelector } from 'hooks/common/useBulkSelector'

import {
  CookieConsentOption,
  CountryCode,
  USA_STATES_WITH_COOKIES,
} from 'root-constants/common'

const useSelectors = () =>
  useBulkSelector({
    userCountryCode: selectUserCountryCode,
    cookieConsent: selectCookieConsent,
    cookieConsentCountries: selectCookieConsentCountries,
    stateCode: selectUserStateCode,
  })

export const useCookieConsentAnswer = () => {
  const { userCountryCode, cookieConsent, cookieConsentCountries, stateCode } =
    useSelectors()

  const isEUUser = cookieConsentCountries.includes(
    userCountryCode.toUpperCase(),
  )
  const isUSAUser = userCountryCode === CountryCode.UNITED_STATES.toUpperCase()

  const shouldApplyCookieConsent = useMemo(() => {
    const isUSAWithCookieConsent =
      isUSAUser && USA_STATES_WITH_COOKIES.includes(stateCode)

    return isEUUser || isUSAWithCookieConsent
  }, [isEUUser, isUSAUser, stateCode])

  const {
    hasAdvertisingAndTargetingCookie,
    hasFunctionalCookie,
    hasAnalyticsAndPerformanceCookie,
  } = {
    hasAdvertisingAndTargetingCookie: (cookieConsent || []).includes(
      CookieConsentOption.ADVERTISING_AND_TARGETING,
    ),
    hasFunctionalCookie: (cookieConsent || []).includes(
      CookieConsentOption.FUNCTIONAL_COOKIES,
    ),
    hasAnalyticsAndPerformanceCookie: (cookieConsent || []).includes(
      CookieConsentOption.ANALYTICS_AND_PERFORMANCE,
    ),
  }

  const isPersonalDataAllowed =
    (shouldApplyCookieConsent && hasAdvertisingAndTargetingCookie) ||
    !shouldApplyCookieConsent

  return {
    isEUUser,
    hasAdvertisingAndTargetingCookie,
    hasFunctionalCookie,
    hasAnalyticsAndPerformanceCookie,
    cookieConsent,
    shouldApplyCookieConsent,
    isPersonalDataAllowed,
  }
}
